// The call co-pilot's panel (module 13; the screen's artboards A to D, H and I; milestone 11 check 7).
//
// A chromeless page at /copilot, shown by the desktop app in a slim window beside a call, always on
// top. It reads the call once (GET /api/copilot/call), listens on the messenger's socket for the
// co-pilot's pushes, and reads the call again whenever the socket reconnects, because the hub
// replays nothing. Silence and "Show anyway" are this window's alone (L15). The panel never starts
// or stops a recording: when the bot is refused, the desktop app records by itself and the panel
// says so (L18).

import { ActionButton, ActionInput } from '../parts/ActionButton.js';
import { useCallback, useEffect, useReducer, useRef, useState, type ReactNode, type RefObject } from 'react';
import { api, ask, Unauthenticated, type AskResult, type Store } from '../api.js';
import { answerParts } from '../AskBar.js';
import { kindWord } from '../records/shared.js';
import { useMessengerSocket } from '../messenger/ws.js';
import { copilot, copilotEventOf, refusalWords, type CardOutcome, type CopilotCard } from './api.js';
import { EXAMPLE, EXAMPLE_STORE, EXAMPLE_WORDS, exampleAnswerer, wantsExample } from './example.js';
import { Icon } from './icons.js';
import {
  approvalParts, askFailureWords, awaitsLocalRecording, clockOf, dayOf, hearing, leadOf, LOCAL_RECORDING_READ_MS, noticeOf, orderCards, panelReducer, panelStart, reachedDayLimit, sourceParts, statusOf, STOP_HINT, titleOf, viewOf,
  type Notice, type PanelAction, type PanelState,
} from './panel-state.js';
import { fetchRundown, type RundownView } from '../company/api.js';
import { Rundown } from '../company/Rundown.js';
import './panel.css';

type Boot = { kind: 'booting' } | { kind: 'signed_out' } | { kind: 'no_store' } | { kind: 'error'; message: string } | { kind: 'ready'; store: Store };

export function CopilotPanel() {
  const [boot, setBoot] = useState<Boot>({ kind: 'booting' });
  // `/copilot?example`: the panel as it is beside a call, drawn from made-up cards and asking the
  // server for nothing, so it opens anywhere and can be shown to anyone. It says what it is.
  const example = typeof window !== 'undefined' && wantsExample(window.location.search);
  useEffect(() => {
    if (example) return;
    let cancelled = false;
    api.me()
      .then((me) => {
        if (cancelled) return;
        // The commercial store: the co-pilot is for client calls (L11).
        const store = me.stores.find((s) => s.kind === 'commercial');
        setBoot(store ? { kind: 'ready', store } : { kind: 'no_store' });
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        setBoot(e instanceof Unauthenticated ? { kind: 'signed_out' } : { kind: 'error', message: refusalWords(e) });
      });
    return () => { cancelled = true; };
  }, [example]);

  if (example) return <ExamplePanel />;
  if (boot.kind === 'ready') return <PanelLive store={boot.store} />;
  return (
    <div className="copilot mc">
      <div className="cp-screen">
        <p className="cp-mark">Cobrain</p>
        {boot.kind === 'booting' ? <p className="cp-say">Reading the call…</p> : null}
        {boot.kind === 'signed_out' ? <><p className="cp-say">Sign in to Cobrain to see the co-pilot.</p><a className="button" href="/auth/login">Sign in with Microsoft</a></> : null}
        {boot.kind === 'no_store' ? <p className="cp-say">Your account is not in Cobrain's commercial store.</p> : null}
        {boot.kind === 'error' ? <p className="cp-say">Cobrain could not load. {boot.message}</p> : null}
      </div>
    </div>
  );
}

// One answerer for the page, kept outside the component so its turn survives a redraw: the second question gets the second answer.
const answerExample = exampleAnswerer();

/**
 * What the example hands the panel's face: nothing of the server's. Its ask box is the live one handed an answerer of its own, because
 * the live lane wants a login and a store and the example has neither, so a question typed here never leaves the page. Its buttons work
 * as they do beside a call, on the example's own state through the panel's own reducer (until 2026-09-21 "Used it", "Not now" and "Duck"
 * were handed nothing and did nothing, on a page made to be shown to people). Its dashboard link has nowhere to go: there is no client.
 * Apart from the component so the test can read what is handed down without a DOM.
 */
export function exampleFace(state: PanelState, dispatch: (action: PanelAction) => void): FaceProps {
  return {
    state, store: EXAMPLE_STORE, answer: answerExample, dashboardHref: null,
    onSilence: () => dispatch({ type: 'silence' }), onShow: () => dispatch({ type: 'show' }), onShowAnyway: () => dispatch({ type: 'show-anyway' }),
    onOutcome: async (cardId, outcome) => dispatch({ type: 'outcome', cardId, outcome }),
    onRetry: () => {},
  };
}

/** The example, with its own word above it: the same panel, drawing nothing real. */
export function ExamplePanel() {
  const [state, dispatch] = useReducer(panelReducer, EXAMPLE);
  return (
    <div className="cp-example mc">
      <p className="cp-example-w">{EXAMPLE_WORDS}</p>
      <PanelFace {...exampleFace(state, dispatch)} />
    </div>
  );
}

function PanelLive({ store }: { store: Store }) {
  const [state, dispatch] = useReducer(panelReducer, panelStart);
  const tenant = store.tenantId;

  const read = useCallback(async () => {
    try {
      dispatch({ type: 'loaded', view: await copilot.call(tenant) });
    } catch (e: unknown) {
      if (e instanceof Unauthenticated) { window.location.href = '/auth/login'; return; }
      dispatch({ type: 'failed', message: refusalWords(e) });
    }
  }, [tenant]);

  useEffect(() => { void read(); }, [read]);
  // A push about a call this page does not hold: a new call has started, so read it.
  useEffect(() => { if (state.reread > 0) void read(); }, [state.reread, read]);
  useMessengerSocket(
    tenant,
    (raw) => {
      const event = copilotEventOf(raw as unknown);
      if (event) dispatch({ type: 'event', event, at: new Date().toISOString() });
    },
    // Every (re)connection reads the call again: pushes sent while the socket was down are gone.
    (open) => { if (open) void read(); },
  );

  // Whether this computer has started recording after a refusal is in the read only (no push carries it).
  const awaiting = awaitsLocalRecording(state.call);
  useEffect(() => {
    if (!awaiting) return;
    const timer = window.setInterval(() => void read(), LOCAL_RECORDING_READ_MS);
    return () => window.clearInterval(timer);
  }, [awaiting, read]);

  const view = viewOf(state);
  useEffect(() => { document.title = titleOf(view); }, [view]);

  const outcome = useCallback(async (cardId: string, o: CardOutcome) => {
    await copilot.outcome(tenant, cardId, o);
    dispatch({ type: 'outcome', cardId, outcome: o });
  }, [tenant]);

  return (
    <PanelFace
      state={state} store={store}
      onSilence={() => dispatch({ type: 'silence' })} onShow={() => dispatch({ type: 'show' })} onShowAnyway={() => dispatch({ type: 'show-anyway' })}
      onOutcome={outcome} onRetry={() => void read()}
    />
  );
}

export interface FaceProps {
  state: PanelState;
  store: Store;
  onSilence: () => void;
  onShow: () => void;
  onShowAnyway: () => void;
  onOutcome: (cardId: string, outcome: CardOutcome) => Promise<void>;
  onRetry: () => void;
  /** Who answers a typed question. Left out beside a call, where it is the ask bar's lane; the example hands in its own. */
  answer?: typeof ask;
  /** Where "Open the client dashboard" goes. Left out beside a call, where it is the company's record page; null in the example, which
      has no client: the words are drawn and lead nowhere, rather than to a real page for a company that does not exist. */
  dashboardHref?: string | null;
}

/** Everything the panel draws, from its state alone. */
export function PanelFace({ state, store, onSilence, onShow, onShowAnyway, onOutcome, onRetry, answer, dashboardHref }: FaceProps) {
  const view = viewOf(state);
  if (view === 'loading') return <div className="copilot mc"><div className="cp-screen"><p className="cp-mark">Cobrain</p><p className="cp-say">Reading the call…</p></div></div>;
  if (view === 'failed') {
    return (
      <div className="copilot mc">
        <div className="cp-screen">
          <p className="cp-mark">Cobrain</p>
          <p className="cp-say">Cobrain could not read the call just now. {state.error}</p>
          <ActionButton reading type="button" className="opt" onClick={onRetry}>Try again</ActionButton>
        </div>
      </div>
    );
  }
  if (view === 'off') {
    return <div className="copilot mc"><div className="cp-off">The co-pilot is not on for your account.</div></div>;
  }
  // C: the bar names no client and shows nothing from the call, because it stays on screen and a share would show it.
  if (view === 'silenced') {
    const listening = !!state.call && hearing(state.call);
    return (
      <div className="copilot collapsed mc">
        <div className="cp-bar" role="status">
          <span className="cp-bar-t"><b>Co-pilot hidden</b>{listening ? <span className="cp-still">still listening</span> : null}</span>
          <ActionButton reading type="button" className="opt" onClick={onShow}><Icon name="eye" />Show</ActionButton>
        </div>
      </div>
    );
  }
  // D: someone on the call started sharing a screen, so the panel hid itself; one click brings it back (L15).
  if (view === 'shared') {
    const listening = !!state.call && hearing(state.call);
    return (
      <div className="copilot collapsed mc">
        <div className="cp-bar" role="status">
          <span className="cp-bar-t"><Icon name="share" className="cp-bar-ico" /><b>Hidden</b> <span className="muted">while a screen is shared</span>{listening ? <span className="cp-still">still listening</span> : null}</span>
          <ActionButton reading type="button" className="opt" onClick={onShowAnyway}><Icon name="eye" />Show anyway</ActionButton>
        </div>
      </div>
    );
  }

  const call = state.call;
  const status = statusOf(call);
  const notice = noticeOf(call);
  const cards = orderCards(state.cards);
  const lead = leadOf(call, cards.length);
  const company = call?.company ?? null;
  const dashboard = !company ? null : dashboardHref === null ? (
    <span className="link cp-dash" aria-disabled="true">Open the client dashboard<Icon name="ext" /></span>
  ) : (
    // The company's record page (the client dashboard, U16, is not built). A new window, so the panel stays beside the call.
    <a className="link cp-dash" href={dashboardHref ?? `/companies/${encodeURIComponent(company.id)}`} target="_blank" rel="noopener">Open the client dashboard<Icon name="ext" /></a>
  );

  return (
    <div className="copilot mc">
      <header className="cp-head">
        <div className="cp-r1">
          <span className="cp-client">{company ? company.name : 'No client call'}</span>
          {/* "Duck", not "Silence": the button moves the window out of the way and nothing else. Cobrain
              records the call throughout, and only a Cobrain user can stop that (L23), so no word on this
              screen may suggest the panel decides what is heard or kept. */}
          <ActionButton reading type="button" className="opt cp-sil" onClick={onSilence}><Icon name="eyeoff" />Duck</ActionButton>
        </div>
        {/* Red is live: a dot while something records, and the words beside it. The dark plate is the clock's
            and is not borrowed here. The row wraps, so a long line never pushes the link out of the window. */}
        <div className={`cp-r2 cp-tone-${status.tone}`}>
          <span className="cp-status">{status.tone === 'rec' ? <i className="live" /> : null}{status.tone === 'stopped' ? <Icon name="alert" /> : null}{status.text}</span>
          {status.note ? <span className="cp-note">{status.note}</span> : null}
          {dashboard}
        </div>
        {call?.recordingLocally && !notice ? <p className="cp-stop">{STOP_HINT}</p> : null}
      </header>
      {notice ? <BotNotice notice={notice} at={state.botAt} /> : null}
      <p className="cp-share"><Icon name="screen" />Sharing your whole screen shows this panel. Share a window instead.</p>
      <section className="cp-body" aria-label="What Cobrain found" aria-live="polite">
        {lead ? <p className="cp-empty">{lead}</p> : null}
        {/* Before anything is found, the short rundown of the client (pipeline RC5; U354): the stake, who is in the room,
            what is owed and what moved, in the Calendar brief's words. Not in the example, which has no client. */}
        {company && cards.length === 0 && dashboardHref !== null ? <CopilotRundown store={store.tenantId} org={company.id} event={call?.event ?? null} /> : null}
        {cards.map((c) => <CardView key={c.id} card={c} onOutcome={onOutcome} />)}
        {state.error ? <p className="cp-empty cp-err">Cobrain could not read the call again just now. {state.error} <ActionButton reading type="button" className="dotted" onClick={onRetry}>Try again</ActionButton></p> : null}
      </section>
      <AskDock store={store} answer={answer} />
    </div>
  );
}

/** The short rundown, read once per client: the panel's window opens links in a new tab, so the call keeps its place. */
function CopilotRundown({ store, org, event }: { store: string; org: string; event: string | null }) {
  const [view, setView] = useState<RundownView | null>(null);
  useEffect(() => {
    let cancelled = false;
    fetchRundown(store, event ? { org, event } : { org }).then((v) => { if (!cancelled) setView(v); }, () => { /* the panel stands without it */ });
    return () => { cancelled = true; };
  }, [store, org, event]);
  return view?.rundown ? <div className="cp-rundown"><Rundown data={view.rundown} cut="short" go={null} /></div> : null;
}

function BotNotice({ notice, at }: { notice: Notice; at: string | null }) {
  return (
    <div className={notice.tone === 'lost' ? 'cp-bot lost' : 'cp-bot'} role={notice.tone === 'lost' ? 'alert' : 'status'}>
      <div className="cp-bot-t">
        {notice.icon === 'rec' ? <i className="live" aria-hidden="true" /> : <Icon name={notice.icon} />}
        <b>{notice.title}</b>
        {at ? <span className="cp-bot-at">{clockOf(at)}</span> : null}
      </div>
      {notice.detail ? <p className="cp-bot-s lead">{notice.detail}</p> : null}
      {notice.hint ? <p className="cp-bot-s">{notice.hint}</p> : null}
    </div>
  );
}

/** The two answers a card takes (L8: kept, and the apprentice's training set). */
function Outcomes({ card, onOutcome, only }: { card: CopilotCard; onOutcome: FaceProps['onOutcome']; only?: 'dismiss' }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const choose = async (o: CardOutcome) => {
    setBusy(true); setError(null);
    try { await onOutcome(card.id, o); }
    catch (e: unknown) {
      if (e instanceof Unauthenticated) { window.location.href = '/auth/login'; return; }
      setError(refusalWords(e));
    } finally { setBusy(false); }
  };
  if (only === 'dismiss') {
    return (
      <>
        <ActionButton type="button" className={card.outcome === 'dismissed' ? 'opt chosen' : 'opt'} disabled={busy || card.outcome === 'dismissed'} onClick={() => void choose('dismissed')}>{card.outcome === 'dismissed' ? <Icon name="check" /> : null}{card.outcome === 'dismissed' ? 'Dismissed' : 'Dismiss'}</ActionButton>
        {error ? <span className="cc-err">{error}</span> : null}
      </>
    );
  }
  return (
    <>
      <ActionButton type="button" className={card.outcome === 'used' ? 'opt lead used' : 'opt lead'} aria-pressed={card.outcome === 'used'} disabled={busy} onClick={() => void choose('used')}>
        {card.outcome === 'used' ? <Icon name="check" /> : null}Used it
      </ActionButton>
      <ActionButton type="button" className={card.outcome === 'dismissed' ? 'opt chosen' : 'opt'} aria-pressed={card.outcome === 'dismissed'} disabled={busy} onClick={() => void choose('dismissed')}>
        {card.outcome === 'dismissed' ? <Icon name="check" /> : null}Not now
      </ActionButton>
      {error ? <span className="cc-err">{error}</span> : null}
    </>
  );
}

function Heard({ who, words }: { who: string; words: string }) {
  return <p className="cc-heard"><b>{who}</b> “{words}”</p>;
}

/** A card's kind, and the word for an answer already given: the state is never colour or fade alone. */
function CardTop({ kind, state, children }: { kind: string; state?: string | null; children?: ReactNode }) {
  return (
    <div className="cc-top">
      <span className="cc-kind">{children}{kind}</span>
      {state ? <span className="cc-state">{state}</span> : null}
    </div>
  );
}

/** One card, as the screen's legend draws each kind. The approved words come first: they are what the
    call needs, and anything before them pushes them down the window. */
export function CardView({ card, onOutcome }: { card: CopilotCard; onOutcome: FaceProps['onOutcome'] }) {
  const done = card.outcome === 'dismissed';
  const cls = (kind: string) => `cc cc-${kind}${done ? ' cc-done' : ''}`;
  if (card.kind === 'answer') {
    return (
      <article className={cls('answer')}>
        <CardTop kind="Approved to say" state={done ? 'Not now' : null} />
        {card.reply ? <p className="cc-words">{card.reply.body}</p> : null}
        {card.heard ? <Heard who="They said:" words={card.heard} /> : card.objection ? <p className="cc-heard"><b>Objection:</b> {card.objection.body}</p> : null}
        {card.reply ? <Parts parts={approvalParts(card.reply)} /> : null}
        <div className="cc-opts"><Outcomes card={card} onOutcome={onOutcome} /></div>
      </article>
    );
  }
  if (card.kind === 'notes') {
    return (
      <article className={cls('notes')}>
        <div className="cc-top">
          <span className="cc-kind">Open notes</span>
          <span className="cc-flag">not approved</span>
        </div>
        <p className="cc-rule">Not the playbook's words. From what you can read.</p>
        {(card.lines ?? []).map((l, i) => (
          <div className="cc-line" key={`${l.source.signalId}-${i}`}>
            {l.otherClient ? <p className="cc-other">Another client's term, {l.otherClient}</p> : null}
            <p>{l.text}</p>
            <Parts parts={sourceParts(l.source)} />
          </div>
        ))}
      </article>
    );
  }
  if (card.kind === 'fact') {
    return (
      <article className={cls('fact')}>
        <CardTop kind="Fact" state={done ? 'Not now' : null}><Icon name="doc" /></CardTop>
        {card.fact ? <p className="cc-title">{card.fact.text}</p> : null}
        {card.heard ? <Heard who="They said:" words={card.heard} /> : null}
        {card.fact ? <Parts parts={[card.fact.source]} /> : null}
        <div className="cc-opts"><Outcomes card={card} onOutcome={onOutcome} /></div>
      </article>
    );
  }
  // A warning, quiet: only against a rule written in the playbook (L2).
  return (
    <article className={cls('warn')}>
      <CardTop kind="Off the playbook" state={done ? 'Dismissed' : null}><Icon name="alert" /></CardTop>
      {card.rule ? <p className="cc-title">{card.rule.body}</p> : null}
      {card.heard ? <p className="cc-heard cc-you"><b>You said:</b> “{card.heard}”</p> : null}
      {done ? null : <div className="cc-opts"><Outcomes card={card} onOutcome={onOutcome} only="dismiss" /></div>}
    </article>
  );
}

/** A meta line: its parts laid out side by side, never joined into one string by middle dots (U28). */
function Parts({ parts }: { parts: string[] }) {
  return <p className="cc-src">{parts.map((t, i) => <span key={i}>{t}</span>)}</p>;
}

/** `limit`: the no-answer is the day's limit, a stop, and not a failure that may pass. */
export interface Asked { question: string; at: string; result: AskResult | null; busy: boolean; error: string | null; limit?: boolean }

/** A source's words for a meta line: the lane's own label split where it joins its parts, else the kind, the person and the day. */
export function sourceWords(s: AskResult['sources'][number]): string[] {
  const parts = s.line ? s.line.split(' · ') : [kindWord(s.kind), s.who, dayOf(s.at)];
  return parts.map((t) => (t ?? '').trim()).filter((t) => t.length > 0);
}

/**
 * A typed question's answer, drawn as the ask bar draws it (module 10 U118): its paragraphs, then, apart from them, what Cobrain says
 * under an answer its own check took a sentence from (module 05 R49), a quiet line that is never part of the answer and never an error,
 * then what was read. Exported for the test: the dock reaches it only after a call to the lane.
 */
export function TypedAnswer({ result }: { result: AskResult }) {
  const parts = answerParts(result);
  return (
    <>
      {parts.paragraphs.map((p, i) => <p key={i} className="cc-ans">{p}</p>)}
      {parts.note ? <p className="cc-left-out" data-ask="left-out">{parts.note}</p> : null}
      <div className="cc-srcs">
        {/* The lane's line is the whole label ("Email · Will · 4 Sep"), so it is drawn alone, its parts side by side (U28). Until U118 the kind, the
            person and the date were put in front of it as well and every source read twice, as the ask bar's did until 2026-09-20. */}
        {result.sources.map((s, i) => <Parts key={i} parts={sourceWords(s)} />)}
        {result.notRead.map((n, i) => <span key={`n${i}`} className="cc-src not-read">{n}</span>)}
        {result.sources.length === 0 && result.notRead.length === 0 ? <span className="cc-src">nothing on record</span> : null}
      </div>
    </>
  );
}

/** The card above the ask box: the question, then the answer, the wait for one, or why there is none. A long answer scrolls inside the card,
    under the row that carries the close button, so the way to put the answer away never leaves the window; the card keeps its whole edge,
    which a row stuck inside a scrolling dock would not. What it says when there is no answer is the panel's own sentence (`askFailureWords`),
    whole: nothing is put in front of it and nothing of the lane's after it. What it is handed is all it draws, so the test draws it; the
    one thing it reads for itself is its own scroller. */
export function AskedCard({ asked, onClose }: { asked: Asked; onClose: () => void }) {
  const scroller = useScroller();
  return (
    <article className="cc cc-reply" aria-live="polite">
      <div className="cc-top">
        <span className="cc-kind">You asked</span>
        <ActionButton reading type="button" className="cc-x" aria-label="Close the answer" onClick={onClose}><Icon name="x" /></ActionButton>
      </div>
      {/* A group with a name, and a place in the tab order while there is something to scroll: a long answer is then read by key in any
          browser, not only where the browser makes a bare scroller reachable by itself, and a short one adds no stop between the close
          button and the ask box. `more` draws the line at the foot that says the answer goes on. */}
      <div ref={scroller.ref} className={scroller.below ? 'cc-scroll more' : 'cc-scroll'} tabIndex={scroller.scrolls ? 0 : undefined} role="group" aria-label="The answer">
        <p className="cc-q">{asked.question}</p>
        {asked.busy ? <p className="cc-gist">Reading what Cobrain has on that…</p> : null}
        {/* No answer is a state, so it is words and form: the head's own mark, and a heavier line; an answer's first paragraph and the
            waiting line are the plain ink this would otherwise share. The mark is red only for the day's limit, which is a stop. A
            failure that may pass is not one, and the box under it still holds the question to send again. */}
        {asked.error ? <p className={asked.limit ? 'cc-gist cc-none cc-stop' : 'cc-gist cc-none'} data-ask="no-answer"><Icon name="alert" /><span>{asked.error}</span></p> : null}
        {asked.result ? <TypedAnswer result={asked.result} /> : null}
      </div>
    </article>
  );
}

/** What the answer's scroller has to scroll, read off the element: after every draw (an answer arriving changes what there is to scroll
    without changing the box), on every scroll, and when the box itself changes with the window. `below` is the cue at the foot. It was
    first drawn in the stylesheet alone, as the line under the row still is, and could not be: a strip that moves with the words stops a
    fraction of a pixel short of the foot of a box whose height is not a whole number, and a sliver of the line showed at rest. */
function useScroller(): { ref: RefObject<HTMLDivElement | null>; scrolls: boolean; below: boolean } {
  const ref = useRef<HTMLDivElement | null>(null);
  const [state, setState] = useState({ scrolls: false, below: false });
  const read = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    const rest = el.scrollHeight - el.clientHeight;
    const next = { scrolls: rest > 1, below: rest - el.scrollTop > 1 };
    setState((was) => (was.scrolls === next.scrolls && was.below === next.below ? was : next));
  }, []);
  useEffect(read);
  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    el.addEventListener('scroll', read, { passive: true });
    const seen = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(read);
    seen?.observe(el);
    return () => { el.removeEventListener('scroll', read); seen?.disconnect(); };
  }, [read]);
  return { ref, ...state };
}

/** B: a typed question, unheard by the call, answered by the ask bar's lane (D74) unless it is handed another answerer, which only the
    example does; the answer sits above the box until closed. Exported for the test, which reads what the example hands it. */
export function AskDock({ store, answer }: { store: Store; answer?: typeof ask | undefined }) {
  const [text, setText] = useState('');
  const [asked, setAsked] = useState<Asked | null>(null);

  const submit = async (e: { preventDefault: () => void }) => {
    e.preventDefault();
    const question = text.trim();
    if (asked?.busy) return;
    if (!question) return;
    const at = new Date().toISOString();
    setAsked({ question, at, result: null, busy: true, error: null });
    try {
      // Beside a call this is the ask bar's own route and no other, written out so that it can be read: what the bar refuses to answer
      // and what its pass takes out of an answer are then the panel's too (module 05 R44; apps/api/src/rails/never-say-output.test.ts
      // reads this very line). Only the example hands in an answerer, and the example's asks nothing of any server or model.
      const result = answer ? await answer(store.tenantId, question) : await ask(store.tenantId, question);
      setAsked((a) => (a && a.at === at ? { ...a, result, busy: false } : a));
      setText('');
    } catch (err: unknown) {
      if (err instanceof Unauthenticated) { window.location.href = '/auth/login'; return; }
      // The panel's own sentence, never the lane's: the lane words a refusal for the ask bar's page (a day's allowance in dollars, a
      // provider's failure as the provider put it), and this window sits beside a client call.
      setAsked((a) => (a && a.at === at ? { ...a, busy: false, error: askFailureWords(err), limit: reachedDayLimit(err) } : a));
      // At the day's limit the box is emptied, as it is once answered. The question is on the card, and a box still holding it with a live
      // arrow says "press again" under words that say "not today". Any other failure leaves the words in the box, to send again or to cut.
      if (reachedDayLimit(err)) setText('');
    }
  };

  return (
    <>
      {asked ? (
        <div className="cp-dock">
          <AskedCard asked={asked} onClose={() => setAsked(null)} />
        </div>
      ) : null}
      <form className="cp-ask" onSubmit={(e) => void submit(e)}>
        <label className={text ? 'cp-field typed' : 'cp-field'}>
          {/* Enter asks, as the ask bar's does: a key sent by automation does not submit a form by itself. While an answer is awaited the
              box is read-only, not disabled: a disabled box drops the focus it had, and the next question then needed a click first. */}
          <ActionInput aria-label="Ask Cobrain" placeholder="Ask Cobrain…" value={text} onChange={(e) => setText(e.target.value)} readOnly={asked?.busy ?? false} aria-busy={asked?.busy ?? false}
                 onKeyDown={(e) => { if (e.key === 'Enter' && !e.nativeEvent.isComposing) void submit(e); }} />
          <ActionButton type="submit" className="cp-send" aria-label="Ask" disabled={!text.trim() || (asked?.busy ?? false)}><Icon name="send" /></ActionButton>
        </label>
        <p className="cp-hint">Typed, never heard on the call. Answered from the record.</p>
      </form>
    </>
  );
}
